// src/pages/deals/DealsInsights.tsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import ScrollToTop from "../../../core/common/ScrollToTop";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LineChart,
  Line,
  CartesianGrid,
  Sector,
} from "recharts";
import moment from "moment";
import { fetchAllDeals, fetchDealsByUserId, Deal } from "../../../api/deal";
import { useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";
import { Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Tip from "../../../core/common/tooltip";

// ====== PERIOD + STATUS TYPES (UNIFIED WITH LEADS/CONTACTS) ======
type Period =
  | "global"
  | "week"
  | "thisMonth"
  | "lastMonth"
  | "threeMonths"
  | "sixMonths"
  | "thisYear"
  | "lastYear"
  | "overall";

const periodOptions = [
  { value: "global" as const, label: "Use Global Filter" },
  { value: "week" as const, label: "Last 1 Week" },
  { value: "thisMonth" as const, label: "This Month" },
  { value: "lastMonth" as const, label: "Last Month" },
  { value: "threeMonths" as const, label: "Past 3 Months" },
  { value: "sixMonths" as const, label: "Past 6 Months" },
  { value: "thisYear" as const, label: "This Year" },
  { value: "lastYear" as const, label: "Last Year" },
  { value: "overall" as const, label: "Overall" },
];

const STATUS_KEYS = ["open", "won", "lost", "Closed"] as const;
type StatusKey = (typeof STATUS_KEYS)[number];

const STATUS_COLORS: Record<StatusKey | "unknown", string> = {
  won: "#4caf50",
  lost: "#f44336",
  open: "#9c27b0",
  Closed: "#9e9e9e",
  unknown: "#607d8b",
};

interface OwnerActivityRow {
  owner: string;
  open: number;
  won: number;
  lost: number;
  Closed: number;
  total: number;
}

// Legend items for owner stacked bar
const legendItems: { key: keyof typeof STATUS_COLORS; label: string }[] = [
  { key: "won", label: "Won" },
  { key: "open", label: "Open" },
  { key: "lost", label: "Lost" },
  { key: "Closed", label: "Closed" },
];

export default function DealsInsights() {
  const route = all_routes;
  const userId = useSelector((s: RootState) => s.auth.user_id);

  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(false);
  const [useAllUsers, setUseAllUsers] = useState(false);

  // ====== GLOBAL + SECTION-WISE PERIODS (MATCH LEADS/CONTACTS) ======
  const [globalPeriod, setGlobalPeriod] = useState<Period>("thisMonth");
  const [piePeriod, setPiePeriod] = useState<Period>("global");
  const [trendPeriod, setTrendPeriod] = useState<Period>("global");
  const [ownerPeriod, setOwnerPeriod] = useState<Period>("global");
  const [productPeriod, setProductPeriod] = useState<Period>("global");

  // Chart / UI State
  const [productModalVisible, setProductModalVisible] = useState(false);
  const [drilldownProduct, setDrilldownProduct] = useState<string | null>(null);

  const [hiddenSlices, setHiddenSlices] = useState<Set<string>>(new Set());
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const [hiddenOwnerBars, setHiddenOwnerBars] = useState<Set<string>>(
    new Set()
  );
  const [hiddenProducts, setHiddenProducts] = useState<Set<string>>(new Set());

  // ====== DATE RANGE UTILS (UNIFIED) ======
  const getDateRange = useCallback((period: Period) => {
    const now = new Date();
    let start: Date;
    let end: Date = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59
    );

    switch (period) {
      case "week":
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "thisMonth":
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "lastMonth":
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      case "threeMonths":
        start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        break;
      case "sixMonths":
        start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        break;
      case "thisYear":
        start = new Date(now.getFullYear(), 0, 1);
        break;
      case "lastYear":
        start = new Date(now.getFullYear() - 1, 0, 1);
        end = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
        break;
      case "overall":
      default:
        start = new Date(0);
        break;
    }

    return { start, end };
  }, []);

  // Helper: filter deals by a local period using globalPeriod fallback
  const getFilteredDeals = useCallback(
    (localPeriod: Period) => {
      let effectivePeriod =
        localPeriod === "global" ? globalPeriod : localPeriod;

      if (effectivePeriod === "overall") {
        return deals;
      }

      const { start, end } = getDateRange(effectivePeriod);

      return deals.filter((d) => {
        if (!d.created_at) return false;
        const dt = new Date(d.created_at);
        return dt >= start && dt <= end;
      });
    },
    [deals, globalPeriod, getDateRange]
  );

  // For showing global date range in header
  const { start: globalStart, end: globalEnd } = useMemo(
    () => getDateRange(globalPeriod),
    [globalPeriod, getDateRange]
  );

  // ====== LOAD DEALS ======
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        let data: Deal[] = [];
        if (useAllUsers) {
          data = await fetchAllDeals();
        } else {
          if (!userId) {
            data = [];
          } else {
            data = await fetchDealsByUserId(userId);
          }
        }
        setDeals(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load deals for insights", err);
        setDeals([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId, useAllUsers]);
  // ====== GLOBAL FILTERED DEALS (for KPI, upcoming, etc.) ======
  const globallyFilteredDeals = useMemo(
    () => getFilteredDeals("global"),
    [getFilteredDeals]
  );

  const totalDeals = globallyFilteredDeals.length;

  // Status counts (global period)
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      won: 0,
      lost: 0,
      open: 0,
      Closed: 0,
    };
    globallyFilteredDeals.forEach((d) => {
      const s = (d.status || "unknown").toString();
      if (counts[s] !== undefined) counts[s] += 1;
    });
    return counts;
  }, [globallyFilteredDeals]);

  // ====== PIE CHART (Deal Status Distribution) ======
  const filteredPieDeals = useMemo(
    () => getFilteredDeals(piePeriod),
    [getFilteredDeals, piePeriod]
  );

  const statusPieDataForChart = useMemo(() => {
    const counts: Record<StatusKey, number> = {
      open: 0,
      won: 0,
      lost: 0,
      Closed: 0,
    };

    filteredPieDeals.forEach((d) => {
      const s = d.status as StatusKey;
      if (counts[s] !== undefined) counts[s]++;
    });

    return STATUS_KEYS.map((k) => ({
      name: k,
      value: counts[k],
    })).filter((i) => i.value > 0);
  }, [filteredPieDeals]);

  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  const toggleSlice = (name: string) => {
    setHiddenSlices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(name)) newSet.delete(name);
      else newSet.add(name);
      return newSet;
    });
  };

  const visiblePieData = useMemo(
    () => statusPieDataForChart.filter((item) => !hiddenSlices.has(item.name)),
    [statusPieDataForChart, hiddenSlices]
  );

  const renderActiveShape = (props: any) => {
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
    } = props;

    return (
      <g>
        <text
          x={cx}
          y={cy}
          dy={8}
          textAnchor="middle"
          fill="#333"
          fontSize={14}
        >
          {props.payload.name}
        </text>

        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 8}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  };

  // ====== OWNER ACTIVITY (stacked bar) ======
  const filteredOwnerDeals = useMemo(
    () => getFilteredDeals(ownerPeriod),
    [getFilteredDeals, ownerPeriod]
  );

  const ownerActivityFiltered = useMemo(() => {
    const map: Record<string, OwnerActivityRow> = {};

    filteredOwnerDeals.forEach((d) => {
      const owner = d.leads?.users?.name || "Unassigned";
      if (!map[owner]) {
        map[owner] = {
          owner,
          open: 0,
          won: 0,
          lost: 0,
          Closed: 0,
          total: 0,
        };
      }

      const s = d.status as StatusKey;
      map[owner][s] = (map[owner][s] || 0) + 1;
      map[owner].total++;
    });

    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredOwnerDeals]);

  const toggleOwnerBar = (key: string) => {
    setHiddenOwnerBars((prev) => {
      const set = new Set(prev);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return set;
    });
  };

  // ====== PRODUCT REVENUE (pie + table) ======
  const filteredProductDeals = useMemo(
    () => getFilteredDeals(productPeriod),
    [getFilteredDeals, productPeriod]
  );

  const productStatsFiltered = useMemo(() => {
    const map: Record<
      string,
      {
        revenue: number;
        open: number;
        won: number;
        lost: number;
        Closed: number;
        total: number;
      }
    > = {};

    filteredProductDeals.forEach((d) => {
      const name = d.product?.product_name || "Unknown Product";
      if (!map[name]) {
        map[name] = {
          revenue: 0,
          open: 0,
          won: 0,
          lost: 0,
          Closed: 0,
          total: 0,
        };
      }
      const value = Number(d.deal_value) || 0;
      map[name].revenue += value;

      const status = d.status as StatusKey;
      map[name][status] = (map[name][status] || 0) + 1;
      map[name].total++;
    });

    return Object.entries(map)
      .map(([product, stats]) => ({ product, ...stats }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [filteredProductDeals]);

  const productChartData = useMemo(
    () =>
      productStatsFiltered
        .map((p) => ({
          name: p.product,
          value: p.revenue,
        }))
        .filter((p) => p.value > 0),
    [productStatsFiltered]
  );

  const toggleProductSlice = (name: string) => {
    setHiddenProducts((prev) => {
      const set = new Set(prev);
      if (set.has(name)) set.delete(name);
      else set.add(name);
      return set;
    });
  };

  const visibleProductData = useMemo(
    () => productChartData.filter((p) => !hiddenProducts.has(p.name)),
    [productChartData, hiddenProducts]
  );

  const PRODUCT_COLORS = useMemo(
    () => [
      "#4e79a7",
      "#f28e2b",
      "#e15759",
      "#76b7b2",
      "#59a14f",
      "#edc948",
      "#b07aa1",
      "#ff9da7",
      "#9c755f",
      "#bab0ac",
    ],
    []
  );

  // ====== TREND (daily/weekly line) ======
  const filteredTrendDeals = useMemo(
    () => getFilteredDeals(trendPeriod),
    [getFilteredDeals, trendPeriod]
  );

  const trendData = useMemo(() => {
    const effective = trendPeriod === "global" ? globalPeriod : trendPeriod;
    const { start, end } = getDateRange(effective);

    const startMoment = moment(start);
    const endMoment = moment(end);
    const days = endMoment.diff(startMoment, "days") + 1;

    const weekly = days > 90;
    const buckets: Record<string, number> = {};

    filteredTrendDeals.forEach((d) => {
      if (!d.created_at) return;
      const created = moment(d.created_at);
      const key = weekly
        ? created.startOf("isoWeek").format("YYYY-MM-DD")
        : created.format("YYYY-MM-DD");
      buckets[key] = (buckets[key] || 0) + 1;
    });

    const result: { date: string; count: number }[] = [];

    if (weekly) {
      let cur = startMoment.clone().startOf("isoWeek");
      while (cur.isSameOrBefore(endMoment)) {
        const k = cur.format("YYYY-MM-DD");
        result.push({ date: k, count: buckets[k] || 0 });
        cur.add(1, "week");
      }
    } else {
      let cur = startMoment.clone();
      while (cur.isSameOrBefore(endMoment)) {
        const k = cur.format("YYYY-MM-DD");
        result.push({ date: k, count: buckets[k] || 0 });
        cur.add(1, "day");
      }
    }

    return result;
  }, [filteredTrendDeals, trendPeriod, globalPeriod, getDateRange]);

  // ====== UPCOMING FOLLOW-UPS / DUE DATES (GLOBAL PERIOD) ======
  const upcoming = useMemo(() => {
    const today = moment().startOf("day");
    const cutoff = moment().add(30, "days").endOf("day");

    return globallyFilteredDeals
      .map((d) => {
        const fu = d.follow_up_date ? moment(d.follow_up_date) : null;
        const due = d.due_date ? moment(d.due_date) : null;

        const nextDate = fu || due;

        return nextDate && nextDate.isBetween(today, cutoff, undefined, "[]")
          ? {
              deal_id: d.deal_id,
              company: d.contact?.company_name || "N/A",
              follow_up_date: d.follow_up_date,
              due_date: d.due_date,
              owner: d.leads?.users?.name || "Unassigned",
              status: d.status,
              sortDate: nextDate,
            }
          : null;
      })
      .filter(Boolean)
      .sort((a, b) => a!.sortDate.diff(b!.sortDate));
  }, [globallyFilteredDeals]);

  // ====== DRILLDOWN MODAL DATA (GLOBAL PERIOD) ======
  const openProductDrilldown = useCallback((productName: string) => {
    setDrilldownProduct(productName);
    setProductModalVisible(true);
  }, []);

  const closeProductDrilldown = useCallback(() => {
    setProductModalVisible(false);
    setDrilldownProduct(null);
  }, []);

  const drilldownData = useMemo(() => {
    if (!drilldownProduct) return [];
    return globallyFilteredDeals.filter((d) => {
      const pName = d.product?.product_name || "Unknown Product";
      return pName === drilldownProduct;
    });
  }, [globallyFilteredDeals, drilldownProduct]);

  // ====== REUSABLE PERIOD DROPDOWN (MATCH LEADS/CONTACTS) ======
  // const renderFilterDropdown = (
  //   value: Period,
  //   onChange: (v: Period) => void,
  //   placeholder = "Select Period"
  // ) => (
  //   <div className="dropdown">
  //     <button
  //       className="btn btn-light btn-sm dropdown-toggle"
  //       data-bs-toggle="dropdown"
  //     >
  //       {periodOptions.find((p) => p.value === value)?.label || placeholder}
  //     </button>

  //     <div className="dropdown-menu dropdown-menu-end">
  //       {periodOptions.map((opt) => (
  //         <button
  //           key={opt.value}
  //           className="dropdown-item"
  //           onClick={() => onChange(opt.value)}
  //         >
  //           {opt.label}
  //         </button>
  //       ))}
  //     </div>
  //   </div>
  // );
  const renderFilterDropdown = (
    value: any,
    onChange: (val: any) => void,
    placeholder: string = "Select Period"
  ) => (
    <Dropdown className="dropdown">
      <Dropdown.Toggle variant="outline-secondary" size="sm" className="btn-sm">
        {periodOptions.find((p) => p.value === value)?.label || placeholder}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {periodOptions.map((opt) => (
          <Dropdown.Item key={opt.value} onClick={() => onChange(opt.value)}>
            {opt.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  if (loading && deals.length === 0) {
    return (
      <>
        <div className="page-wrapper">
          <div className="page-body">
            <div className="text-center py-5">
              <i className="ti ti-loader-3 ti-spin fs-48 text-primary"></i>
              <p className="mt-3">Loading Deal Insights...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageLoadProgressBar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* ===== HEADER CARD (MATCH LEADS/CONTACTS) ===== */}
          <div className="card shadow-sm border-0 rounded-4 p-3 mb-4 bg-light-subtle">
            <div className="row align-items-center">
              {/* Left Title Block */}
              <div className="col-md-6 d-flex align-items-start gap-3">
                <div className="text-primary fs-3 mt-1">
                  <i className="bi bi-cash-coin"></i>
                </div>

                <div>
                  <h3 className="mb-1 fw-semibold d-flex align-items-center gap-2">
                    Deal Insights Dashboard
                    <Tip
                      text="Analytics overview of deals by status, owner, product and time period."
                      placement="top"
                    >
                      <i
                        className="bi bi-info-circle ms-1 text-secondary"
                        style={{ cursor: "pointer", fontSize: 15 }}
                      />
                    </Tip>
                  </h3>

                  <p className="text-muted small mb-1">
                    Complete analytics of deals with unified global time
                    filtering
                  </p>

                  <ol className="breadcrumb mb-0 small">
                    <li className="breadcrumb-item">
                      <Link to={route.deals} className="text-decoration-none">
                        Deals
                      </Link>
                    </li>
                    <li className="breadcrumb-item active">Deal Insights</li>
                  </ol>
                </div>
              </div>

              {/* Right Global Filter */}
              <div className="col-md-6 text-md-end mt-3 mt-md-0">
                <div className="d-inline-flex align-items-center gap-2 px-3 py-2 bg-white shadow-sm border rounded-3">
                  <i className="bi bi-globe2 text-primary me-2"></i>
                  <div className="text-start me-2">
                    <div className="fw-semibold small">Global Time Filter</div>
                    <div className="text-muted small">
                      Applies to all sections
                    </div>
                    <div className="text-muted small">
                      {moment(globalStart).format("DD MMM YYYY")} —{" "}
                      {moment(globalEnd).format("DD MMM YYYY")}
                    </div>
                  </div>

                  {renderFilterDropdown(
                    globalPeriod,
                    setGlobalPeriod,
                    "Choose Global Period"
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ===== MAIN GRID ===== */}
          <div className="row g-3">
            {/* KPI: Total Deals */}
            <div className="col-12 col-md-3">
              <div className="card shadow-sm border-0 dashboard-card h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <div className="stat-icon bg-primary bg-opacity-10 text-primary me-2">
                      <i className="bi bi-graph-up me-2"></i>
                    </div>
                    <h6 className="text-muted mb-0">Total Deals</h6>
                  </div>
                  <h2 className="fw-bold mb-1">{totalDeals}</h2>
                  <span className="text-muted small">
                    in selected global range
                  </span>
                </div>
              </div>
            </div>

            {/* KPI: Status Cards */}
            <div className="col-12 col-md-9">
              <div className="row g-3">
                {STATUS_KEYS.map((s) => (
                  <div key={s} className="col-12 col-sm-6 col-md-3">
                    <div
                      className="card shadow-sm border-0 h-100 dashboard-card position-relative"
                      style={{
                        borderLeft: `4px solid ${
                          STATUS_COLORS[s] || STATUS_COLORS.unknown
                        }`,
                      }}
                    >
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <small className="text-muted fw-semibold">
                            {s.toUpperCase()}
                          </small>
                          <div
                            className="stat-icon"
                            style={{
                              backgroundColor: `${STATUS_COLORS[s]}20`,
                              color: STATUS_COLORS[s],
                            }}
                          >
                            <i className="ti ti-chart-bar" />
                          </div>
                        </div>

                        <div className="fw-bold" style={{ fontSize: 26 }}>
                          {statusCounts[s] || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ===== PIE: DEAL STATUS DISTRIBUTION ===== */}
            <div className="col-12 col-md-6">
              <div className="card shadow-sm border-0 h-100">
                {/* HEADER */}
                <div className="card-header bg-white border-0 pb-0 d-flex justify-content-between align-items-center">
                  {/* LEFT */}
                  <div className="d-flex align-items-center gap-2">
                    <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                      <i className="bi bi-graph-up text-primary"></i>
                      Deal Status Distribution
                    </h5>

                    <Tip
                      text="Pie chart showing deal status breakdown based on the selected section period or global filter."
                      placement="top"
                    >
                      <i
                        className="bi bi-info-circle text-secondary"
                        style={{ cursor: "pointer", fontSize: 15 }}
                      />
                    </Tip>
                  </div>

                  <div
                    style={{
                      transform: "scale(0.9)",
                      transformOrigin: "right center",
                    }}
                  >
                    {renderFilterDropdown(
                      piePeriod,
                      setPiePeriod,
                      "Status Period"
                    )}
                  </div>
                </div>

                {/* BODY */}
                <div className="card-body pt-2">
                  {deals.length === 0 || statusPieDataForChart.every(x => x.value === 0) ? (
                    <div className="text-center py-5">
                      <i className="bi bi-database-x text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for Deal Status Distribution
                      </p>
                    </div>
                  ) : (
                    <div
                      style={{
                        height: 300,
                        position: "relative",
                      }}
                    >
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={visiblePieData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={60}
                            outerRadius={95}
                            paddingAngle={2}
                            activeIndex={activeIndex ?? undefined}
                            onMouseEnter={onPieEnter}
                            activeShape={renderActiveShape}
                            label={({ name, value }) =>
                              value > 0 ? `${name}: ${value}` : ""
                            }
                          >
                            {visiblePieData.map((entry, idx) => (
                              <Cell
                                key={`cell-${idx}`}
                                fill={STATUS_COLORS[entry.name as StatusKey]}
                                opacity={hiddenSlices.has(entry.name) ? 0.3 : 1}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>

                      {/* TOTAL INSIDE DONUT */}
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 22,
                            fontWeight: 700,
                          }}
                        >
                          {statusPieDataForChart.reduce(
                            (sum, x) => sum + x.value,
                            0
                          )}
                        </div>
                        <div className="text-muted small">total deals</div>
                      </div>
                    </div>
                  )}

                  {/* LEGEND */}
                  {deals.length > 0 && !statusPieDataForChart.every(x => x.value === 0) && (
                    <div className="mt-3 d-flex flex-wrap align-items-center gap-3">
                    {statusPieDataForChart.map((item, idx) => {
                      const isHidden = hiddenSlices.has(item.name);

                      return (
                        <div
                          key={idx}
                          onClick={() => toggleSlice(item.name)}
                          className="d-flex align-items-center gap-1"
                          style={{
                            cursor: "pointer",
                            opacity: isHidden ? 0.4 : 1,
                            userSelect: "none",
                          }}
                        >
                          <span
                            style={{
                              width: 12,
                              height: 12,
                              backgroundColor:
                                STATUS_COLORS[item.name as StatusKey],
                              borderRadius: "50%",
                              display: "inline-block",
                            }}
                          ></span>

                          <span
                            className="text-capitalize fw-semibold"
                            style={{
                              fontSize: 13,
                            }}
                          >
                            {item.name}
                          </span>

                          <span className="text-muted small">
                            ({item.value})
                          </span>
                        </div>
                      );
                    })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ===== TREND LINE ===== */}
            <div className="col-12 col-md-6">
              <div className="card shadow-sm border-0 h-100">
                {/* HEADER */}
                <div className="card-header bg-white border-0 pb-0 d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <h6 className="card-title mb-0 d-flex align-items-center">
                      <i className="bi bi-graph-up-arrow text-primary me-2"></i>
                      Deal Timeline Chart
                    </h6>
                    <Tip
                      text="Line chart showing daily or weekly deal creation trend based on the selected period or global filter."
                      placement="top"
                    >
                      <i
                        className="bi bi-info-circle ms-2 text-secondary me-2"
                        style={{ cursor: "pointer", fontSize: 15 }}
                      />
                    </Tip>
                  </div>

                  {renderFilterDropdown(
                    trendPeriod,
                    setTrendPeriod,
                    "Timeline Period"
                  )}
                </div>

                {/* CHART */}
                <div className="card-body pt-2">
                  {deals.length === 0 || trendData.length === 0 || trendData.every(d => d.count === 0) ? (
                    <div className="text-center py-5">
                      <i className="bi bi-database-x text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for Deal Timeline Chart
                      </p>
                    </div>
                  ) : (
                    <div style={{ height: 300 }}>
                      <ResponsiveContainer>
                        <LineChart data={trendData}>
                          <CartesianGrid strokeDasharray="3 3" />

                          <XAxis
                            dataKey="date"
                            tickFormatter={(d) => moment(d).format("MM/DD")}
                          />

                          <YAxis />

                          <Tooltip
                            labelFormatter={(d) => moment(d).format("LL")}
                            formatter={(v: any) => [v, "Deals"]}
                          />

                          <Line
                            type="monotone"
                            dataKey="count"
                            stroke="#3f51b5"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 7 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ===== OWNER ACTIVITY ===== */}
            <div className="col-12 col-md-6">
              <div className="card shadow-sm border-0 h-100">
                {/* HEADER */}
                <div className="card-header bg-white border-0 pb-0 d-flex justify-content-between align-items-center">
                  <h6 className="card-title mb-0 d-flex align-items-center">
                    <i className="bi bi-people text-primary me-2"></i>
                    Lead Owner Activity
                    <Tip
                      text="Stacked bar showing each lead owner’s activity based on deal status within the selected period or global filter."
                      placement="top"
                    >
                      <i
                        className="bi bi-info-circle ms-2 text-secondary me-2"
                        style={{ cursor: "pointer", fontSize: 15 }}
                      />
                    </Tip>
                  </h6>

                  {renderFilterDropdown(
                    ownerPeriod,
                    setOwnerPeriod,
                    "Owner Period"
                  )}
                </div>

                {/* CHART */}
                <div className="card-body pt-2">
                  {deals.length === 0 || ownerActivityFiltered.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-database-x text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for Lead Owner Activity
                      </p>
                    </div>
                  ) : (
                    <div style={{ height: 340 }}>
                      <ResponsiveContainer>
                        <BarChart
                          data={ownerActivityFiltered}
                          layout="vertical"
                          margin={{ left: 80, right: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />

                          <XAxis type="number" />
                          <YAxis type="category" dataKey="owner" width={160} />

                          <Tooltip />

                          {!hiddenOwnerBars.has("won") && (
                            <Bar
                              dataKey="won"
                              stackId="a"
                            fill={STATUS_COLORS.won}
                          />
                        )}

                        {!hiddenOwnerBars.has("lost") && (
                          <Bar
                            dataKey="lost"
                            stackId="a"
                            fill={STATUS_COLORS.lost}
                          />
                        )}

                        {!hiddenOwnerBars.has("open") && (
                          <Bar
                            dataKey="open"
                            stackId="a"
                            fill={STATUS_COLORS.open}
                          />
                        )}

                        {!hiddenOwnerBars.has("Closed") && (
                          <Bar
                            dataKey="Closed"
                            stackId="a"
                            fill={STATUS_COLORS.Closed}
                          />
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  )}

                  {/* INTERACTIVE LEGEND */}
                  {deals.length > 0 && ownerActivityFiltered.length > 0 && (
                    <div className="mt-3 d-flex flex-wrap align-items-center gap-4">
                    {legendItems.map((item) => {
                      const isHidden = hiddenOwnerBars.has(item.key);

                      return (
                        <div
                          key={item.key}
                          className="d-flex align-items-center gap-1"
                          onClick={() => toggleOwnerBar(item.key)}
                          style={{
                            cursor: "pointer",
                            opacity: isHidden ? 0.4 : 1,
                            userSelect: "none",
                            transition: "0.2s",
                          }}
                        >
                          <span
                            style={{
                              width: 12,
                              height: 12,
                              backgroundColor: STATUS_COLORS[item.key],
                              borderRadius: "50%",
                              display: "inline-block",
                            }}
                          ></span>

                          <span
                            className="fw-semibold text-capitalize"
                            style={{ fontSize: 13 }}
                          >
                            {item.label}
                          </span>
                        </div>
                      );
                    })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ===== PRODUCT REVENUE DONUT ===== */}
            <div className="col-12 col-md-6">
              <div className="card shadow-sm border-0 h-100">
                {/* Header */}
                <div className="card-header bg-white border-0 pb-0 d-flex justify-content-between align-items-center">
                  <h6 className="card-title mb-0 d-flex align-items-center">
                    <i className="bi bi-cash-stack text-primary me-2"></i>
                    Product Revenue
                    <Tip
                      text="Donut chart showing top product revenue based on the selected period or global filter."
                      placement="top"
                    >
                      <i
                        className="bi bi-info-circle ms-2 text-secondary me-2"
                        style={{ cursor: "pointer", fontSize: 15 }}
                      />
                    </Tip>
                  </h6>

                  {renderFilterDropdown(
                    productPeriod,
                    setProductPeriod,
                    "Product Period"
                  )}
                </div>

                {/* Donut Chart */}
                <div className="card-body pt-2">
                  {deals.length === 0 || visibleProductData.length === 0 || visibleProductData.every(p => p.value === 0) ? (
                    <div className="text-center py-5">
                      <i className="bi bi-database-x text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for Product Revenue
                      </p>
                    </div>
                  ) : (
                    <div
                      style={{
                        height: 300,
                        position: "relative",
                      }}
                    >
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={visibleProductData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={50}
                            outerRadius={90}
                            paddingAngle={2}
                            label={(entry) => (entry.value > 0 ? entry.name : "")}
                          >
                            {visibleProductData.map((entry, idx) => (
                              <Cell
                                key={`cell-${idx}`}
                                fill={PRODUCT_COLORS[idx % PRODUCT_COLORS.length]}
                                opacity={hiddenProducts.has(entry.name) ? 0.3 : 1}
                              />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v: any) => v.toLocaleString()} />
                        </PieChart>
                      </ResponsiveContainer>

                      {/* Center Total Revenue */}
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 20,
                            fontWeight: 700,
                          }}
                        >
                          ₹
                          {visibleProductData
                            .reduce((a, b) => a + b.value, 0)
                            .toLocaleString()}
                        </div>
                        <div className="text-muted small">total</div>
                      </div>
                    </div>
                  )}

                  {/* Interactive Legend */}
                  {deals.length > 0 && visibleProductData.length > 0 && !visibleProductData.every(p => p.value === 0) && (
                  <div className="mt-3 d-flex flex-wrap align-items-center gap-3">
                    {productChartData.map((p, idx) => {
                      const isHidden = hiddenProducts.has(p.name);

                      return (
                        <div
                          key={idx}
                          className="d-flex align-items-center gap-1"
                          onClick={() => toggleProductSlice(p.name)}
                          style={{
                            cursor: "pointer",
                            opacity: isHidden ? 0.4 : 1,
                            userSelect: "none",
                          }}
                        >
                          <span
                            style={{
                              width: 12,
                              height: 12,
                              backgroundColor:
                                PRODUCT_COLORS[idx % PRODUCT_COLORS.length],
                              borderRadius: "50%",
                            }}
                          ></span>

                          <span
                            style={{ fontSize: 13 }}
                            className="fw-semibold"
                          >
                            {p.name}
                          </span>
                        </div>
                      );
                    })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ===== PRODUCT TABLE + DRILLDOWN ===== */}
            <div className="col-12">
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white border-0 d-flex align-items-center justify-content-between">
                  <h6 className="card-title mb-0 d-flex align-items-center">
                    <i className="bi bi-table text-primary me-2"></i>
                    Product Status &amp; Revenue (Detailed)
                    <Tip
                      text="Detailed breakdown of product revenue and deal statuses. Click a product name to view its associated deals."
                      placement="top"
                    >
                      <i
                        className="bi bi-info-circle ms-2 text-secondary"
                        style={{ cursor: "pointer", fontSize: 15 }}
                      />
                    </Tip>
                  </h6>
                </div>

                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-hover table-sm align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th>
                            Revenue
                            <i className="bi bi-arrow-down-up ms-1 small text-muted"></i>
                          </th>
                          <th className="text-success">
                            Won <span className="small">(W)</span>
                          </th>
                          <th className="text-primary">
                            Open <span className="small">(O)</span>
                          </th>
                          <th className="text-danger">
                            Lost <span className="small">(L)</span>
                          </th>
                          <th className="text-secondary">
                            Closed <span className="small">(C)</span>
                          </th>
                          <th>Total Deals</th>
                        </tr>
                      </thead>

                      <tbody>
                        {productStatsFiltered.map((p) => (
                          <tr key={p.product}>
                            <td>
                              <button
                                className="btn btn-link p-0 fw-semibold"
                                onClick={() => openProductDrilldown(p.product)}
                              >
                                {p.product}
                              </button>
                            </td>

                            <td className="fw-semibold">
                              ₹{p.revenue ? p.revenue.toLocaleString() : 0}
                            </td>

                            <td>
                              {p.won > 0 ? (
                                <span className="badge bg-success bg-opacity-10 text-success">
                                  {p.won}
                                </span>
                              ) : (
                                <span className="text-muted">0</span>
                              )}
                            </td>

                            <td>
                              {p.open > 0 ? (
                                <span className="badge bg-primary bg-opacity-10 text-primary">
                                  {p.open}
                                </span>
                              ) : (
                                <span className="text-muted">0</span>
                              )}
                            </td>

                            <td>
                              {p.lost > 0 ? (
                                <span className="badge bg-danger bg-opacity-10 text-danger">
                                  {p.lost}
                                </span>
                              ) : (
                                <span className="text-muted">0</span>
                              )}
                            </td>

                            <td>
                              {p.Closed > 0 ? (
                                <span className="badge bg-secondary bg-opacity-10 text-secondary">
                                  {p.Closed}
                                </span>
                              ) : (
                                <span className="text-muted">0</span>
                              )}
                            </td>

                            <td className="fw-bold">{p.total}</td>
                          </tr>
                        ))}

                        {productStatsFiltered.length === 0 && (
                          <tr>
                            <td
                              colSpan={7}
                              className="text-center text-muted py-3"
                            >
                              <i className="bi bi-card-list me-2"></i>
                              No product data available for the selected range.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== UPCOMING FOLLOW-UPS / DUE DATES ===== */}
            <div className="col-12 col-md-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                  <h6 className="card-title mb-0 d-flex align-items-center">
                    <i className="bi bi-calendar-week text-primary me-2"></i>
                    Upcoming Follow-Ups / Due Dates
                    <Tip
                      text="Shows deals that have upcoming follow-ups or due dates within the next 30 days, within the global period range."
                      placement="top"
                    >
                      <i
                        className="bi bi-info-circle ms-2 text-secondary"
                        style={{ cursor: "pointer", fontSize: 15 }}
                      />
                    </Tip>
                  </h6>
                </div>

                <div
                  className="card-body pt-2"
                  style={{
                    maxHeight: 360,
                    overflowY: "auto",
                  }}
                >
                  <table className="table table-hover table-sm mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Deal</th>
                        <th>Owner</th>
                        <th>Follow Up</th>
                        <th>Due Date</th>
                        <th>Status</th>
                        <th className="text-end">In</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upcoming.map((u) => {
                        const follow = u?.follow_up_date
                          ? moment(u.follow_up_date)
                          : null;
                        const due = u?.due_date ? moment(u.due_date) : null;
                        const target = follow || due;
                        const daysLeft = target
                          ? target.diff(moment(), "days")
                          : null;

                        const days = daysLeft ?? -999;

                        let badgeClass =
                          daysLeft === null
                            ? "bg-secondary"
                            : days <= 1
                            ? "bg-danger"
                            : days <= 3
                            ? "bg-warning text-dark"
                            : "bg-success";

                        return (
                          <tr key={u?.deal_id}>
                            <td className="fw-semibold">{u?.company}</td>
                            <td>{u?.owner}</td>

                            <td>
                              {follow ? (
                                moment(follow).format("DD MMM YYYY")
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>

                            <td>
                              {due ? (
                                moment(due).format("DD MMM YYYY")
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>

                            <td>
                              <span className="badge bg-light text-dark border small">
                                {u?.status}
                              </span>
                            </td>

                            <td className="text-end">
                              {target ? (
                                <span className={`badge ${badgeClass}`}>
                                  {days < 0
                                    ? "Overdue"
                                    : days === 0
                                    ? "Today"
                                    : days === 1
                                    ? "Tomorrow"
                                    : `${days} days`}
                                </span>
                              ) : (
                                <span className="text-muted small">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}

                      {upcoming.length === 0 && (
                        <tr>
                          <td
                            colSpan={6}
                            className="text-center text-muted py-3"
                          >
                            <i className="bi bi-calendar-x me-2"></i>
                            No upcoming follow-ups or due dates
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* ===== PRODUCT DRILLDOWN MODAL ===== */}
          {productModalVisible && (
            <>
              <div
                className="modal fade show d-block"
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
              >
                <div
                  className="modal-dialog modal-xl"
                  role="document"
                  style={{ maxWidth: 900 }}
                >
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        Deals for: {drilldownProduct ?? ""}
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={closeProductDrilldown}
                      />
                    </div>
                    <div className="modal-body">
                      <div className="table-responsive">
                        <table className="table table-sm table-striped">
                          <thead>
                            <tr>
                              <th>Deal ID</th>
                              <th>Company</th>
                              <th>Owner</th>
                              <th>Value</th>
                              <th>Status</th>
                              <th>Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {drilldownData.map((d) => (
                              <tr key={d.deal_id}>
                                <td>{d.deal_id}</td>
                                <td>{d.contact?.company_name || "-"}</td>
                                <td>{d.leads?.users?.name || "-"}</td>
                                <td>
                                  {(
                                    Number.parseFloat(
                                      (d.deal_value as any) || "0"
                                    ) || 0
                                  ).toLocaleString()}
                                </td>
                                <td>{d.status}</td>
                                <td>
                                  {d.created_at
                                    ? moment(d.created_at).format("DD MMM YYYY")
                                    : "-"}
                                </td>
                              </tr>
                            ))}
                            {drilldownData.length === 0 && (
                              <tr>
                                <td colSpan={6} className="text-muted">
                                  No deals for this product
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={closeProductDrilldown}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* backdrop */}
              <div
                className="modal-backdrop fade show"
                onClick={closeProductDrilldown}
              />
            </>
          )}
        </div>
      </div>
      <ScrollToTop />
    </>
  );
}
